import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import { Camera, Loader2, X, ChevronLeft, ChevronRight, Calendar, Filter, Maximize2, ChevronDown, ChevronUp, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { getPublicGalleries, type PublicGalleryItem } from "@/services/publicGalleryService";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";

const Gallery: React.FC = () => {
  const { t, i18n } = useTranslation('public');

  const { data: galleryItems, isLoading } = useQuery({
    queryKey: ["public-galleries"],
    queryFn: getPublicGalleries,
  });

  const [activeItem, setActiveItem] = useState<PublicGalleryItem | null>(null);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [selectedAlbum, setSelectedAlbum] = useState<string>("All");
  const [search, setSearch] = useState("");

  const ITEMS_PAGE_SIZE = 4;
  const PHOTOS_PREVIEW_LIMIT = 8;
  const [visibleItemsCount, setVisibleItemsCount] = useState(ITEMS_PAGE_SIZE);
  const [expandedItemIds, setExpandedItemIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    setVisibleItemsCount(ITEMS_PAGE_SIZE);
  }, [selectedAlbum, search]);

  const toggleExpandItem = (id: number) => {
    setExpandedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openLightbox = (item: PublicGalleryItem, startIdx: number) => {
    setActiveItem(item);
    setActivePhotoIdx(startIdx);
  };

  const closeLightbox = () => setActiveItem(null);

  const photos = activeItem?.images && activeItem.images.length > 0
    ? activeItem.images
    : activeItem?.image_url
      ? [{ id: 0, type: "image" as const, image_url: activeItem.image_url, embed_url: null, display_order: 0 }]
      : [];

  const goPrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setActivePhotoIdx((i) => (i - 1 + photos.length) % photos.length);
  };

  const goNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setActivePhotoIdx((i) => (i + 1) % photos.length);
  };

  const albums = useMemo(() => {
    if (!galleryItems) return ["All"];
    const uniqueAlbums = new Set<string>();
    galleryItems.forEach(item => {
      if (item.album) uniqueAlbums.add(item.album);
    });
    return ["All", ...Array.from(uniqueAlbums)];
  }, [galleryItems]);

  const filteredItems = useMemo(() => {
    if (!galleryItems) return [];
    return galleryItems.filter(item => {
      const matchAlbum = selectedAlbum === "All" || item.album === selectedAlbum;
      const matchSearch = (item.title || "").toLowerCase().includes(search.toLowerCase()) || 
                          (item.description || "").toLowerCase().includes(search.toLowerCase());
      return matchAlbum && matchSearch;
    });
  }, [galleryItems, selectedAlbum, search]);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd MMMM yyyy", {
        locale: i18n.language === 'id' ? idLocale : undefined
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <section id="gallery" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-amber-600 bg-clip-text text-transparent">
            {t('gallery.title', 'Galeri Kegiatan')}
          </h2>
          <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
            {t('gallery.description', 'Dokumentasi momen-momen penting dan kegiatan yang telah berlangsung.')}
          </p>
        </div>

        {/* Search & Filter Card */}
        <div className="w-full mb-12 bg-white dark:bg-gray-900 border border-border rounded-2xl shadow-lg p-6 transition-all duration-300 hover:shadow-xl space-y-4">
          <div className="relative w-full">
            <Search
              className={`absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 transition-colors duration-300 ${search ? "text-blue-600" : "text-gray-400"}`}
            />
            <Input
              type="text"
              placeholder={t('gallery.search_placeholder', 'Cari dokumentasi...')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-14 mt-3 mb-6 pr-12 h-12 text-lg rounded-xl border border-border shadow-sm focus-visible:ring-2 focus-visible:ring-blue-500 transition-all duration-300 hover:shadow-md"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>

          {albums.length > 2 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 mr-1">
                <Filter className="w-3.5 h-3.5" /> {t('gallery.filter', 'Filter:')}
              </span>
              {albums.map((album) => (
                <button
                  key={album}
                  onClick={() => setSelectedAlbum(album)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 ${
                    selectedAlbum === album
                      ? "bg-blue-600 text-white shadow shadow-blue-600/30"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {album === "All" ? t('gallery.all_albums', 'Semua Album') : album}
                </button>
              ))}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
            <p className="text-gray-500 dark:text-gray-400 animate-pulse">{t('common.loading', 'Memuat galeri...')}</p>
          </div>
        ) : filteredItems.length > 0 ? (
          <div className="space-y-16">
            {filteredItems.slice(0, visibleItemsCount).map((item) => {
              const allItemPhotos = item.images && item.images.length > 0
                ? item.images
                : item.image_url
                  ? [{ id: 0, type: "image" as const, image_url: item.image_url, embed_url: null, display_order: 0 }]
                  : [];

              if (allItemPhotos.length === 0) return null;

              const isExpanded = expandedItemIds.has(item.id);
              const itemPhotos = isExpanded ? allItemPhotos : allItemPhotos.slice(0, PHOTOS_PREVIEW_LIMIT);
              const hiddenCount = allItemPhotos.length - itemPhotos.length;

              const embedPhotos = itemPhotos.filter(p => p.type === 'embed');
              const imagePhotos = itemPhotos.filter(p => p.type !== 'embed');

              return (
                <div key={item.id} className="pb-16 border-b border-slate-200 dark:border-slate-800 last:border-0 last:pb-0">
                  {/* Item Header */}
                  <div className="mb-8">
                    {item.album && selectedAlbum === "All" && (
                      <span className="inline-block px-2.5 py-1 rounded-sm text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 uppercase tracking-wider mb-3">
                        {item.album}
                      </span>
                    )}
                    <h3 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-2 tracking-tight">
                      {item.title}
                    </h3>
                    {item.created_at && (
                      <div className="flex items-center text-slate-500 dark:text-slate-400 text-sm mb-4">
                        <Calendar className="w-4 h-4 mr-2" />
                        {formatDate(item.created_at)}
                      </div>
                    )}
                    {item.description && (
                      <p className="text-slate-600 dark:text-slate-400 w-full text-base leading-relaxed text-justify">
                        {item.description}
                      </p>
                    )}
                  </div>

                  {/* Embeds - Grid layout */}
                  {embedPhotos.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      {embedPhotos.map((photo, idx) => (
                        <div
                          key={photo.id || `embed-${idx}`}
                          className="relative rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900 w-full aspect-video"
                        >
                          <iframe
                            src={photo.embed_url || ""}
                            className="absolute top-0 left-0 w-full h-full block border-0"
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                          <button
                            type="button"
                            onClick={() => openLightbox(item, allItemPhotos.indexOf(photo))}
                            title={t('gallery.expand', 'Perbesar') as string}
                            className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full backdrop-blur-sm transition-colors z-10"
                          >
                            <Maximize2 className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Masonry Grid for Images */}
                  {imagePhotos.length > 0 && (
                    <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4">
                      {imagePhotos.map((photo, idx) => (
                        <div
                          key={photo.id || `img-${idx}`}
                          onClick={() => openLightbox(item, allItemPhotos.indexOf(photo))}
                          className="break-inside-avoid mb-4 group relative rounded-lg overflow-hidden cursor-pointer border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900"
                        >
                          <img
                            src={photo.image_url || ""}
                            alt={`${item.title || 'Gallery'} - ${idx + 1}`}
                            className="w-full h-auto object-cover transition-opacity duration-300 group-hover:opacity-80"
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {(hiddenCount > 0 || isExpanded) && allItemPhotos.length > PHOTOS_PREVIEW_LIMIT && (
                    <div className="flex justify-center mt-10">
                      <button
                        type="button"
                        onClick={() => toggleExpandItem(item.id)}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-medium bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        {isExpanded ? (
                          <>
                            {t('gallery.show_less', 'Tampilkan lebih sedikit')}
                            <ChevronUp className="w-4 h-4" />
                          </>
                        ) : (
                          <>
                            {t('gallery.show_all_photos', `Lihat semua ${allItemPhotos.length} foto`, { count: allItemPhotos.length })}
                            <ChevronDown className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            <div className="flex flex-col items-center mt-8 gap-4">
              <div className="text-sm text-gray-500 font-medium">
                {t('gallery.showing', { count: visibleItemsCount > filteredItems.length ? filteredItems.length : visibleItemsCount, total: filteredItems.length, defaultValue: `Menampilkan ${visibleItemsCount > filteredItems.length ? filteredItems.length : visibleItemsCount} dari ${filteredItems.length} album` })}
              </div>
              
              <div className="w-48 sm:w-64 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 transition-all duration-500 rounded-full"
                  style={{ width: `${(Math.min(visibleItemsCount, filteredItems.length) / filteredItems.length) * 100}%` }}
                />
              </div>

              {visibleItemsCount < filteredItems.length && (
                <button
                  type="button"
                  onClick={() => setVisibleItemsCount((c) => c + ITEMS_PAGE_SIZE)}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-sm font-bold bg-white dark:bg-gray-800 text-blue-600 border-2 border-blue-100 dark:border-gray-700 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-gray-700 transition-all mt-2"
                >
                  {t('common:buttons.load_more', 'Tampilkan Lebih Banyak')}
                  <ChevronDown className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-24 bg-white dark:bg-gray-900 rounded-3xl shadow-sm border border-dashed border-gray-200 dark:border-gray-800 max-w-2xl mx-auto">
            <div className="inline-flex items-center justify-center p-4 rounded-full bg-gray-50 dark:bg-gray-800 mb-4">
              <Camera className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">Belum ada foto</h3>
            <p className="text-gray-500 dark:text-gray-400">
              {selectedAlbum === "All" 
                ? t('gallery.no_results', 'Belum ada dokumentasi yang ditambahkan.') 
                : t('gallery.no_results_album', `Belum ada dokumentasi untuk album ${selectedAlbum}.`)}
            </p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {activeItem && photos.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center animate-in fade-in duration-300"
          onClick={closeLightbox}
        >
          {/* Top Bar */}
          <div className="absolute top-0 left-0 w-full p-4 md:p-6 flex justify-between items-start z-20 bg-gradient-to-b from-black/80 to-transparent">
            <div className="text-white max-w-3xl pr-12">
              {activeItem.album && (
                <span className="inline-block px-2.5 py-1 rounded bg-blue-600 text-xs font-semibold tracking-wide uppercase mb-3 shadow-lg shadow-blue-500/20">
                  {activeItem.album}
                </span>
              )}
              {activeItem.title && <h3 className="text-2xl md:text-3xl font-bold drop-shadow-md">{activeItem.title}</h3>}
              {activeItem.created_at && (
                <p className="text-gray-300 text-sm mt-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatDate(activeItem.created_at)}
                </p>
              )}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              className="text-gray-400 hover:text-white bg-black/20 hover:bg-white/10 p-3 rounded-full backdrop-blur-md transition-all shrink-0 focus:outline-none focus:ring-2 focus:ring-white/50"
              aria-label="Close lightbox"
            >
              <X className="w-6 h-6 md:w-8 md:h-8" />
            </button>
          </div>

          {/* Main Image Area */}
          <div 
            className="relative w-full h-full flex items-center justify-center pt-24 pb-[200px]" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full max-w-6xl px-4 md:px-16 flex items-center justify-center group/image h-full">
               {photos[activePhotoIdx].type === "embed" ? (
                 <iframe
                    src={photos[activePhotoIdx].embed_url || ""}
                    className="w-full max-w-5xl h-[75vh] rounded-sm shadow-2xl animate-in zoom-in-95 duration-300 border-0 bg-white"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                 />
               ) : (
                 <img
                    src={photos[activePhotoIdx].image_url || ""}
                    alt={activeItem.title || "Gallery photo"}
                    className="max-w-full max-h-full object-contain drop-shadow-2xl rounded-sm animate-in zoom-in-95 duration-300"
                 />
               )}

               {photos.length > 1 && (
                 <>
                   <button
                     onClick={goPrev}
                     className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/90 text-white p-3 md:p-4 rounded-full backdrop-blur-md transition-all opacity-0 group-hover/image:opacity-100 md:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/50 hover:scale-110"
                     aria-label="Previous image"
                   >
                     <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
                   </button>
                   <button
                     onClick={goNext}
                     className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/90 text-white p-3 md:p-4 rounded-full backdrop-blur-md transition-all opacity-0 group-hover/image:opacity-100 md:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/50 hover:scale-110"
                     aria-label="Next image"
                   >
                     <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
                   </button>
                 </>
               )}
            </div>
          </div>

          {/* Bottom Bar: Description & Thumbnails */}
          <div 
            className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black via-black/80 to-transparent pt-12 pb-6 px-4 md:px-8 z-20 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {activeItem.description && (
              <div className="w-full max-w-4xl mb-6">
                <p className="text-gray-200 text-sm md:text-base text-center line-clamp-3 hover:line-clamp-none transition-all duration-300 ease-in-out cursor-pointer drop-shadow-md bg-black/20 p-4 rounded-xl backdrop-blur-sm">
                  {activeItem.description}
                </p>
              </div>
            )}

            {photos.length > 1 && (
              <div className="w-full max-w-5xl overflow-hidden">
                <div className="flex gap-2 justify-center overflow-x-auto py-2 px-4 snap-x scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                  {photos.map((photo, idx) => (
                    <button
                      key={photo.id || idx}
                      onClick={() => setActivePhotoIdx(idx)}
                      className={`relative shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden snap-center transition-all duration-300 focus:outline-none ${
                        activePhotoIdx === idx 
                          ? 'ring-2 ring-blue-500 scale-110 z-10 shadow-lg shadow-blue-500/50' 
                          : 'opacity-50 hover:opacity-100 hover:scale-105'
                      }`}
                    >
                      {photo.type === "embed" ? (
                        <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white/70">
                          <Camera className="w-6 h-6" />
                        </div>
                      ) : (
                        <img
                          src={photo.image_url || ""}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
